import { useState } from 'react';

import { CampaignPreviewThumbnail } from 'modules/campaigns/lib/components/CampaignPreviewThumbnail';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { CheckboxGridCell } from 'ui/indexPages/tables/cells';
import { GridRow } from 'ui/indexPages/tables/grid';
import { wasRecurring } from 'utils/campaigns';
import { isSelectionContent } from 'utils/contexts/selection';
import { CampaignAlertHistorySource } from 'modules/campaigns/lib/components/AlertsHistorySlideOver/types';
import { AlertsHistorySlideOver } from 'modules/campaigns/lib/components/AlertsHistorySlideOver';
import { AlertTakeover } from 'modules/campaigns/lib/takeovers/AlertTakeover';
import { CampaignState, CampaignType, CampaignTypeFilter } from 'modules/domains/campaign';
import { ensureEnum } from 'utils/tsHelpers';

import { CampaignDeliveryTableCell } from '../components/CampaignDeliveryTableCell';
import { CampaignEngagementTableCell } from '../components/CampaignEngagementCell';
import { CampaignLastModifiedCell } from '../components/CampaignLastModifiedCell';
import { CampaignLaunchTableCell } from '../components/CampaignLaunchTableCell';
import { CampaignNameCell } from '../components/CampaignNameCell';
import { CampaignStatusTableCell } from '../components/CampaignStatusTableCell';
import { CAMPAIGN_GRIDS } from '../constants';
import { GraphQLCampaign } from '../domainTypes';
import { User } from '../types';
import { CampaignOverflowMenu } from './CampaignOverflowMenu';
import { useCampaignQueryContext } from './contexts/CampaignQueryContext';
import { useSelectionContext } from './contexts/SelectionContext';
import { SavedAlertsByMedium } from '../../campaigns/alerts/types';
import { Favorite } from '../../../ui/indexPages/Favorite/Favorite';
import { useSelector } from 'react-redux';
import { selectIsCampaignsFavoritesFeatureFlagOn } from 'core/userContext';
import { StyledContainer } from '../CampaignIndexPage.styles';
import { useFavorite } from '../../../ui/indexPages/Favorite/hooks/useFavorite';

type CampaignRowProps = {
  campaign: GraphQLCampaign;
  userInfoLookup: { [key: string]: User };
  hasRecentTriggeredAlerts?: boolean;
  projectAlerts: SavedAlertsByMedium;
  previewThumbnail?: string;
};

export const CampaignRow = ({
  campaign,
  userInfoLookup,
  hasRecentTriggeredAlerts,
  projectAlerts,
  previewThumbnail,
}: CampaignRowProps) => {
  const isCampaignsFavoritesFeatureFlagOn = useSelector(selectIsCampaignsFavoritesFeatureFlagOn);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [showAlertOverride, setShowAlertOverride] = useState(false);
  const {
    isSelected: isFavoriteSelected,
    isVisible,
    onFavoriteClick,
    rowProps,
  } = useFavorite({
    isEnabled: isCampaignsFavoritesFeatureFlagOn,
    isFavorite: campaign.isFavorite,
  });

  const { groupRecurringCampaigns, parentCampaign } = useCampaignQueryContext();
  const { bulk, toggleBulkSelection } = useSelectionContext();

  const isSelected = Boolean(
    bulk.find((item) => isSelectionContent(item) && item.id === campaign.id),
  );
  const shouldLinkToChildrenView =
    groupRecurringCampaigns && wasRecurring(campaign) && !parentCampaign;

  const onViewAlerts = () => setShowAlertHistory(true);

  return (
    <GridRow gridLayout={CAMPAIGN_GRIDS} key={campaign.id} {...rowProps}>
      <StyledContainer>
        <CheckboxGridCell
          data-test={`checkbox-${campaign.id}${isSelected ? '-selected' : ''}`}
          id={campaign.id}
          handleClick={() => toggleBulkSelection(campaign)}
          status={isSelected ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
        />
        <Favorite isVisible={isVisible} isSelected={isFavoriteSelected} onClick={onFavoriteClick} />
      </StyledContainer>

      <CampaignPreviewThumbnail campaign={campaign} previewThumbnail={previewThumbnail} />
      <CampaignNameCell campaign={campaign} groupRecurringCampaigns={shouldLinkToChildrenView} />
      <CampaignStatusTableCell
        campaign={campaign}
        hasRecentTriggeredAlerts={hasRecentTriggeredAlerts}
        onViewAlerts={onViewAlerts}
      />
      <CampaignDeliveryTableCell campaign={campaign} />
      <CampaignEngagementTableCell campaign={campaign} />
      <CampaignLaunchTableCell campaign={campaign} />
      <CampaignLastModifiedCell campaign={campaign} userInfoLookup={userInfoLookup} />
      <CampaignOverflowMenu
        campaign={campaign}
        onViewAlerts={onViewAlerts}
        projectAlerts={projectAlerts}
        isFavoriteSelected={isFavoriteSelected}
        onFavoriteClick={onFavoriteClick}
      />

      {showAlertHistory && (
        <AlertsHistorySlideOver
          campaignName={campaign.name}
          campaignId={campaign.id}
          templateId={campaign.templateId}
          messageMedium={campaign.messageMedium}
          campaignState={campaign.campaignState as unknown as CampaignState}
          campaignType={campaign.campaignTypeDefinition as unknown as CampaignType}
          campaignAlertsEnabled={!!campaign?.campaignAlertsEnabled}
          onClose={() => !showAlertOverride && setShowAlertHistory(false)}
          onEditAlerts={() => {
            setShowAlertOverride(true);
          }}
          source={CampaignAlertHistorySource.CampaignIndex}
        />
      )}

      {showAlertOverride && (
        <AlertTakeover
          campaignData={{
            campaignId: campaign.id,
            name: campaign.name,
            messageMedium: campaign.messageMedium,
            campaignType: ensureEnum(
              campaign.campaignTypeDefinition,
              CampaignTypeFilter,
              CampaignTypeFilter.Triggered,
            ),
          }}
          onTakeoverOpen={(val) => setShowAlertOverride(val)}
        />
      )}
    </GridRow>
  );
};
